import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Radio, RadioGroup, FormControlLabel, Select, MenuItem, CircularProgress, Box, Button, TextField
} from '@mui/material';
import api from '../../services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

const ShopDespatch = () => {
  const [rows, setRows] = useState([]);
  const [karigars, setKarigars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wastagePercentages, setWastagePercentages] = useState({});
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch orders
      const ordersRes = await api.get('/api/shop/orders');
      // Flatten items, but only include those not yet despatched
      const allItems = ordersRes.data.flatMap(order => order.items || []);
      const itemRows = ordersRes.data.flatMap(order =>
        (order.items || [])
          .filter(item => !item.despatchStatus)
          .map(item => ({
            billName: order.billName,
            billNumber: order.billNumber,
            orderDate: order.orderDate ? new Date(order.orderDate).toLocaleDateString() : '',
            deliveryDate: order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString() : '',
            requiredDate: order.requiredDate ? new Date(order.requiredDate).toLocaleDateString() : '',
            orPriority: order.isUrgent ? 'Urgent' : 'Normal',
            salesman: order.agentName,
            itemName: item.itemName,
            itemQty: item.quantity,
            quantityUnit: item.unit,
            itemWeight: item.itemWeight,
            karat: item.karat,
            despatch: false,
            karigar: '',
            wastage: item.wastage || '',
            orderId: order._id,
            itemId: item._id
          }))
      );
      setRows(itemRows);
      // Fetch karigars
      const karigarsRes = await api.get('/api/karigar');
      setKarigars(karigarsRes.data);
    } catch (err) {
      setError('Failed to fetch data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDespatchChange = (index, value) => {
    setRows(rows => rows.map((row, i) => i === index ? { ...row, despatch: value === 'yes' } : row));
  };

  const handleKarigarChange = (index, value) => {
    setRows(rows => rows.map((row, i) => i === index ? { ...row, karigar: value } : row));
  };

  const handleWastageChange = (index, value) => {
    setRows(rows => rows.map((row, i) => i === index ? { ...row, wastage: value } : row));
  };

  const handleDespatch = async (row, idx) => {
    try {
      // Fetch the full order
      const orderRes = await api.get(`/api/shop/orders/${row.orderId}`);
      const order = orderRes.data;
      // Update the specific item
      const updatedItems = order.items.map(item =>
        item._id === row.itemId
          ? { ...item, despatchStatus: true, karigarAbbreviation: row.karigar, wastage: row.wastage }
          : item
      );
      await api.put(`/api/shop/orders/${row.orderId}`, { ...order, items: updatedItems });
      // Re-fetch data so the despatched item is removed
      await fetchData();
    } catch (err) {
      setError('Failed to update despatch status');
    }
  };

  const handleUndoDespatch = async (row) => {
    try {
      // Fetch the full order
      const orderRes = await api.get(`/api/shop/orders/${row.orderId}`);
      const order = orderRes.data;
      // Update the specific item
      const updatedItems = order.items.map(item =>
        item._id === row.itemId
          ? { ...item, despatchStatus: false }
          : item
      );
      await api.put(`/api/shop/orders/${row.orderId}`, { ...order, items: updatedItems });
      // Re-fetch data so the item reappears
      await fetchData();
    } catch (err) {
      setError('Failed to undo despatch status');
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;
  }

  if (error) {
    return <Container maxWidth="xl" sx={{ py: 6 }}><Paper sx={{ p: 4 }}><Typography color="error">{error}</Typography></Paper></Container>;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} variant="outlined" color="primary">Back</Button>
        </Box>
        <Typography variant="h4" gutterBottom color="primary">
          Shop Despatch
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Bill Name</TableCell>
                <TableCell>Bill Number</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Delivery Date</TableCell>
                <TableCell>Required Date</TableCell>
                <TableCell>ORPriority</TableCell>
                <TableCell>Salesman</TableCell>
                <TableCell>Item Name</TableCell>
                <TableCell>Item Qty</TableCell>
                <TableCell>Quantity Unit</TableCell>
                <TableCell>Item Weight</TableCell>
                <TableCell>Karat</TableCell>
                <TableCell>Despatch</TableCell>
                <TableCell>Despatch Status</TableCell>
                <TableCell>Karigar</TableCell>
                <TableCell>Wastage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={row.billNumber + '-' + row.itemName + '-' + idx}>
                  <TableCell>{row.billName}</TableCell>
                  <TableCell>{row.billNumber}</TableCell>
                  <TableCell>{row.orderDate}</TableCell>
                  <TableCell>{row.deliveryDate}</TableCell>
                  <TableCell>{row.requiredDate}</TableCell>
                  <TableCell>{row.orPriority}</TableCell>
                  <TableCell>{row.salesman}</TableCell>
                  <TableCell>{row.itemName}</TableCell>
                  <TableCell>{row.itemQty}</TableCell>
                  <TableCell>{row.quantityUnit}</TableCell>
                  <TableCell>{row.itemWeight}</TableCell>
                  <TableCell>{row.karat}</TableCell>
                  <TableCell>
                    <RadioGroup
                      row
                      value={row.despatch ? 'yes' : 'no'}
                      onChange={e => handleDespatchChange(idx, e.target.value)}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                  </TableCell>
                  <TableCell>
                    {row.despatch ? <Typography variant="caption" color="success.main">Despatched</Typography> : ''}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={row.karigar}
                      onChange={e => handleKarigarChange(idx, e.target.value)}
                      displayEmpty
                      renderValue={selected => selected || 'Select Karigar'}
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value="">
                        <em>Select Karigar</em>
                      </MenuItem>
                      {karigars.map(k => (
                        <MenuItem key={k.abbreviation} value={k.abbreviation}>
                          {k.abbreviation} - {k.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <TextField
                      label="Wastage"
                      name="wastage"
                      value={wastagePercentages[row.billNumber + '-' + row.itemName] || ''}
                      onChange={e => {
                        const value = e.target.value;
                        setWastagePercentages({
                          ...wastagePercentages,
                          [row.billNumber + '-' + row.itemName]: value
                        });
                        setRows(rows => rows.map((r, i) => i === idx ? { ...r, wastage: value } : r));
                      }}
                      size="small"
                      fullWidth
                      disabled={!row.despatch}
                    />
                  </TableCell>
                  <TableCell>
                    {row.despatch && !row.despatchStatus && row.karigar && row.wastage ? (
                      <Button variant="contained" size="small" onClick={() => handleDespatch(row, idx)}>
                        Despatch
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default ShopDespatch;